import { inject, observer } from "mobx-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Loading from "../../../components/Loading";
import MyProfileStore from "../../../modules/MyProfile/store";
import { UserInfoDto } from "../../../modules/MyProfile/types";
import { MessageResultState } from "../../../types/general-types";
import { ALL_PLATFORMS, platformOf } from "../../config/permissionPlatforms";
import { REDESIGN_NS } from "../../i18n";
import { getInitials } from "../../ui/avatar";
import { IconChevronDown, IconSearch } from "../../ui/icons";
import "./profile.css";

/** Backend AX'te eşleşme bulamayınca bu adı yolluyor (AccountService.DefaultModuleName). */
const OTHER_MODULE = "Diğer";

const TR_FOLD: Record<string, string> = {
  "ı": "i", "İ": "i", "ş": "s", "Ş": "s", "ğ": "g", "Ğ": "g",
  "ü": "u", "Ü": "u", "ö": "o", "Ö": "o", "ç": "c", "Ç": "c",
};

/* `"İ".toLowerCase()` birleşik noktalı bir karakter üretip eşleşmeyi bozuyor. */
const fold = (value: string) =>
  value.replace(/[ıİşŞğĞüÜöÖçÇ]/g, (character) => TR_FOLD[character]).toLowerCase();

interface Props {
  MyProfileStore?: MyProfileStore;
}

const ProfileView: FC<Props> = inject("MyProfileStore")(
  observer(({ MyProfileStore: store }: Props) => {
    const { t } = useTranslation(REDESIGN_NS);

    const [user, setUser] = useState<UserInfoDto>();
    const [isLoading, setIsLoading] = useState(true);
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const [query, setQuery] = useState("");

    useEffect(() => {
      store
        ?.getUserInfos()
        .then((response) => {
          if (response.state === MessageResultState.SUCCESS) {
            setUser(response.result);
          }
        })
        // Hata toast'ını interceptor basıyor; burası yalnızca spinner'ı kapatır.
        .catch(() => undefined)
        .then(() => setIsLoading(false));
    }, [store]);

    const permissions = useMemo(() => user?.permissions || [], [user]);

    // Modül adının kaynağı ABUSERROLES.MODULADI; eşleme backend'de yapılıyor.
    const moduleOf = useMemo(() => {
      const map: Record<string, string> = {};
      (user?.permissionModules || []).forEach((module) =>
        (module.permissions || []).forEach((permission) => {
          map[permission] = module.moduleName;
        })
      );
      return map;
    }, [user]);

    const groups = useMemo(() => {
      const needle = fold(query.trim());

      const byPlatform = new Map<string, Map<string, string[]>>();
      ALL_PLATFORMS.forEach((platform) => byPlatform.set(platform, new Map()));

      permissions
        .filter((permission) => {
          if (!needle) return true;
          if (fold(permission).indexOf(needle) >= 0) return true;
          return fold(moduleOf[permission] || OTHER_MODULE).indexOf(needle) >= 0;
        })
        .forEach((permission) => {
          const modules = byPlatform.get(platformOf(permission));
          if (!modules) return;
          const name = moduleOf[permission] || OTHER_MODULE;
          modules.set(name, (modules.get(name) || []).concat(permission));
        });

      return ALL_PLATFORMS.map((platform) => {
        const modules = Array.from(byPlatform.get(platform) || [])
          .map(([name, items]) => ({ name, items }))
          .sort((a, b) => {
            if (a.name === OTHER_MODULE) return 1;
            if (b.name === OTHER_MODULE) return -1;
            return a.name.localeCompare(b.name, "tr");
          });

        const count = modules.reduce((total, module) => total + module.items.length, 0);
        return { platform, modules, count };
      });
    }, [permissions, moduleOf, query]);

    const visibleGroups = query.trim()
      ? groups.filter((group) => group.count > 0)
      : groups;

    const isSearching = query.trim().length > 0;

    const rows: { label: string; value?: string }[] = [
      { label: t("Name Surname"), value: user?.name },
      { label: t("Username"), value: user?.username },
      { label: t("Region"), value: user?.region },
      { label: t("Branch"), value: user?.branch },
      { label: t("Position"), value: user?.position },
    ];

    const pageHead = (
      <div className="sso-rd__page-head">
        <div className="sso-rd__page-head-text">
          <div className="sso-rd__eyebrow">{t("Company Name")}</div>
          <h1 className="sso-rd__page-title">{t("Profile")}</h1>
        </div>
      </div>
    );

    const summary = (
      <div className="sso-rd__profile-left">
        <div className="sso-rd__profile-photo">
          {user?.avatar ? (
            <img src={user.avatar} alt="" />
          ) : (
            <span className="sso-rd__profile-initials">
              {getInitials(user?.name || user?.username)}
            </span>
          )}
        </div>

        <div className="sso-rd__profile-info">
          {rows.map((row) => (
            <div className="sso-rd__profile-row" key={row.label}>
              <span className="sso-rd__profile-label">{row.label}</span>
              <span className="sso-rd__profile-value">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    );

    const permissionPanel = (
      <div>
        <div className="sso-rd__perm-head">
          <span className="sso-rd__perm-title">{t("Permissions")}</span>
          <span className="sso-rd__perm-count">{permissions.length}</span>
        </div>

        <div className="sso-rd__search sso-rd__perm-search">
          <IconSearch size={17} />
          <input
            type="text"
            value={query}
            placeholder={t("Search permission or module...")}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t("Search permission or module...")}
          />
        </div>

        <div className="sso-rd__accordion">
          {visibleGroups.map(({ platform, modules, count }) => {
            const isOpen = isSearching || openGroup === platform;
            // Platform ve modül adları kaynaktan geliyor, çevrilmez.
            const label = t("{{group}} Permissions", { group: platform });

            return (
              <div
                className={"sso-rd__acc-item" + (isOpen ? " is-open" : "")}
                key={platform}
              >
                <button
                  type="button"
                  className="sso-rd__acc-head"
                  onClick={() => setOpenGroup(isOpen ? null : platform)}
                  aria-expanded={isOpen}
                >
                  <span className="sso-rd__acc-name">{label}</span>
                  <span className="sso-rd__acc-badge">{count}</span>
                  <IconChevronDown size={14} className="sso-rd__acc-caret" />
                </button>

                {isOpen && (
                  <div className="sso-rd__acc-body">
                    {modules.length === 0 ? (
                      <div className="sso-rd__perm-empty">
                        {t("No permissions defined.")}
                      </div>
                    ) : (
                      modules.map((module) => (
                        <div className="sso-rd__perm-module" key={module.name}>
                          <div className="sso-rd__perm-module-head">
                            <span className="sso-rd__perm-module-name">
                              {module.name}
                            </span>
                            <span className="sso-rd__perm-module-count">
                              {module.items.length}
                            </span>
                          </div>
                          <div className="sso-rd__perm-chips">
                            {module.items.map((permission) => (
                              <span className="sso-rd__perm-chip" key={permission}>
                                {permission}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );

    return (
      <div className="sso-rd__container">
        {pageHead}

        {/* Kullanıcı gelmeden çizersek boş bilgi satırları ve 0'lı yetki
            grupları görünür; ikisi de veri yokluğu değil, hata gibi okunuyor. */}
        {isLoading ? (
          <Loading />
        ) : (
          <div className="sso-rd__profile">
            {summary}
            {permissionPanel}
          </div>
        )}
      </div>
    );
  })
) as FC<Props>;

export default ProfileView;
